import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00B4D8',
      light: '#48CAE4',
      dark: '#0077B6',
      contrastText: '#0F1117',
    },
    secondary: {
      main: '#2D3A52',
      light: '#3D4F6E',
      dark: '#1E2B3F',
      contrastText: '#E2E8F0',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
      contrastText: '#0F1117',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBB840',
      dark: '#D97706',
      contrastText: '#0F1117',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#60A5FA',
      light: '#93C5FD',
      dark: '#3B82F6',
      contrastText: '#0F1117',
    },
    background: {
      default: '#0F1117',
      paper: '#1A1D27',
    },
    text: {
      primary: '#E2E8F0',
      secondary: '#94A3B8',
    },
    divider: '#2D3748',
    grey: {
      50: '#F8FAFC',
      100: '#E2E8F0',
      200: '#CBD5E1',
      300: '#94A3B8',
      400: '#64748B',
      500: '#475569',
      600: '#334155',
      700: '#2D3748',
      800: '#1E2535',
      900: '#0F1117',
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0F1117',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#12151E',
          color: '#E2E8F0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
          borderBottom: '1px solid #2D3748',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#12151E',
          color: '#E2E8F0',
          borderRight: '1px solid #2D3748',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: '#E2E8F0',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 180, 216, 0.08)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1A1D27',
        },
        elevation1: {
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
        },
        elevation2: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
        },
        elevation3: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#1A1D27',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
          border: '1px solid #2D3748',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
        },
        containedPrimary: {
          backgroundColor: '#00B4D8',
          color: '#0F1117',
          '&:hover': {
            backgroundColor: '#48CAE4',
          },
        },
        containedSecondary: {
          backgroundColor: '#2D3A52',
          color: '#E2E8F0',
          '&:hover': {
            backgroundColor: '#3D4F6E',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1D27',
        },
        indicator: {
          backgroundColor: '#00B4D8',
          height: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: '#94A3B8',
          '&.Mui-selected': {
            color: '#00B4D8',
            fontWeight: 600,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#1E2235',
            color: '#E2E8F0',
            fontWeight: 700,
            fontSize: '0.875rem',
            borderBottom: '2px solid #2D3748',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 180, 216, 0.06)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#2D3748',
          color: '#E2E8F0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2D3748',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00B4D8',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00B4D8',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#94A3B8',
          '&.Mui-focused': {
            color: '#00B4D8',
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          color: '#94A3B8',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#2D3748',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1E2535',
          color: '#E2E8F0',
          fontSize: '0.75rem',
          border: '1px solid #2D3748',
        },
        arrow: {
          color: '#1E2535',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: '#94A3B8',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 180, 216, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 180, 216, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(0, 180, 216, 0.2)',
            },
          },
        },
      },
    },
  },
});

export default theme;
